import { useCallback, useEffect, useState } from "react";
import * as inventoryApi from "@/lib/inventoryApi";

const message = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  // Pydantic returns a list of field errors; show the first rather than "[object Object]".
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the server";

const call = async fn => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return { ok: false, error: message(error) };
  }
};

/**
 * Stock levels, the movement ledger and the valuation, against /api/inventory.
 *
 * `on_hand` is derived server-side from the movement ledger, so nothing here ever
 * patches a level locally: a recorded movement is followed by a re-read of
 * /inventory/stock, and only then does the caller get `ok`.
 *
 * `canValue` is the role gate. Reads are admin|cashier but /inventory/valuation is
 * admin-only, so a cashier must not fire that request at all rather than collect a
 * 403 and paint an empty panel over it.
 */
export function useInventory(token, { canValue = false } = {}) {
  const [stock, setStock] = useState([]);
  const [items, setItems] = useState([]);
  const [low, setLow] = useState([]);
  const [movements, setMovements] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [filters, setFilters] = useState({});
  const [activeOnly, setActiveOnly] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Separates "the catalogue is empty" from "we could not ask". Only the first may
  // render an empty state inviting the operator to add an item.
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await Promise.allSettled([
      inventoryApi.stockLevels(activeOnly, token),
      inventoryApi.lowStock(token),
      inventoryApi.listMovements({ ...filters, limit: 100 }, token),
      // The full catalogue, inactive included, so a ledger row for a retired item
      // still resolves to a name instead of "#14".
      inventoryApi.listItems({ activeOnly: false }, token),
      canValue ? inventoryApi.valuation(token) : Promise.resolve(null),
    ]);
    const [levels, lowRows, ledger, catalogue, value] = results;
    if (levels.status === "fulfilled") setStock(levels.value);
    if (lowRows.status === "fulfilled") setLow(lowRows.value);
    if (ledger.status === "fulfilled") setMovements(ledger.value);
    if (catalogue.status === "fulfilled") setItems(catalogue.value);
    if (value.status === "fulfilled") setValuation(value.value);
    setLoaded(levels.status === "fulfilled");
    // Partial failure is normal — valuation is admin-only while the levels are not.
    // Blanking the whole page because one panel is forbidden is worse.
    const failed = results.find(r => r.status === "rejected");
    setError(failed ? message(failed.reason) : null);
    setLoading(false);
  }, [token, activeOnly, filters, canValue]);

  useEffect(() => {
    load();
  }, [load]);

  // Nothing reports success until the server confirmed AND the page re-read what the
  // server now holds. A movement changes a derived on-hand; only a re-read knows it.
  const withReload = fn => async (...args) => {
    const result = await call(() => fn(...args));
    if (result.ok) await load();
    return result;
  };

  return {
    stock,
    items,
    low,
    movements,
    valuation,
    filters,
    activeOnly,
    error,
    loading,
    loaded,
    setFilters,
    setActiveOnly,
    reload: load,

    addItem: withReload(body => inventoryApi.createItem(body, token)),
    editItem: withReload((itemId, patch) => inventoryApi.updateItem(itemId, patch, token)),
    /** `body.quantity` must already be signed — build it with signedQuantity(). */
    recordMovement: withReload(body => inventoryApi.createMovement(body, token)),
  };
}
